/* global gigya */
import {useCallback, useContext} from 'react'

import Context from '@s-ui/react-context'
import {AuthenticationContext} from '@adv-mt/mt-context-authentication'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

const generateRandomPassword = () => {
  const base = Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2)
  return base.slice(0, 10) + 'A1!'
}

const mapConsentsToGigyaPreferences = consents => {
  const preferences = {}

  Object.entries(consents).forEach(([key, isConsentGranted]) => {
    const [category, consentId] = key.split('.')

    if (consentId) {
      preferences[category] = {...preferences[category], [consentId]: {isConsentGranted}}
      return
    }

    preferences[key] = {isConsentGranted}
  })

  return preferences
}

const useAuthBeforeLead = ({enableAuthBeforeLead = false} = {}) => {
  const {domain, logger} = useContext(Context)
  const {isLogged = false} = useContext(AuthenticationContext)
  const {track} = useAnalyticsTrack()

  const requiresAuth = useCallback(({isPrivate} = {}) => Boolean(isPrivate) && !isLogged, [isLogged])

  const registerAndAuthenticateWithGigya = useCallback(
    async ({email, name}) => {
      const config = domain.get('config')
      const {
        HIT_ORIGINS,
        TRACKING_EVENT_NAMES: {SIGNED_UP, SIGNED_UP_ERROR}
      } = config.get('tracking')

      const autoGeneratedPassword = generateRandomPassword()

      try {
        const [, defaultConsents] = await domain.get('get_default_consents_user_use_case').execute()
        const preferences = defaultConsents ? mapConsentsToGigyaPreferences(defaultConsents) : undefined

        const gigyaRegistrationToken = await new Promise((resolve, reject) => {
          gigya.accounts.initRegistration({
            callback: response => {
              if (response.errorCode === 0) resolve(response.regToken)
              else reject(new Error(response.errorMessage))
            }
          })
        })

        const gigyaRegistrationResponse = await new Promise((resolve, reject) => {
          let settled = false

          gigya.accounts.register({
            email,
            password: autoGeneratedPassword,
            regToken: gigyaRegistrationToken,
            finalizeRegistration: true,
            preferences,
            profile: {firstName: name},
            callback: response => {
              if (settled) return
              settled = true

              if (response.errorCode === 0) {
                resolve(response)
                return
              }

              track({event: SIGNED_UP_ERROR, data: {errorCode: response.errorCode}})
              reject(new Error(response.errorMessage))
            }
          })
        })

        await domain
          .get('login_authentication_use_case')
          .execute({gigyaResponse: gigyaRegistrationResponse.userInfo ?? gigyaRegistrationResponse})

        track({event: SIGNED_UP, data: {hit_origin: HIT_ORIGINS.LEAD_EMAIL}})

        return {userHasBeenRegistered: true, autoGeneratedPassword}
      } catch (error) {
        track({event: SIGNED_UP_ERROR, data: {hit_information: 'generic_error'}})
        logger?.error({
          name: '[ContactSeller]: auth before lead failed',
          message: `[ContactSeller]: ${error?.message}`
        })

        return {userHasBeenRegistered: false}
      }
    },
    [domain, logger, track]
  )

  const registerIfNeeded = useCallback(
    async ({email, name}) => {
      if (!enableAuthBeforeLead || isLogged) return {userHasBeenRegistered: false}

      try {
        const [, result] = await domain.get('check_user_email_exists_user_use_case').execute({email})

        if (result && !result.exists) {
          return registerAndAuthenticateWithGigya({email, name})
        }

        return {userHasBeenRegistered: false}
      } catch (error) {
        logger?.error({
          name: '[ContactSeller]: auth before lead email check failed',
          message: `[ContactSeller]: ${error?.message}`
        })

        return {userHasBeenRegistered: false}
      }
    },
    [domain, enableAuthBeforeLead, isLogged, logger, registerAndAuthenticateWithGigya]
  )

  return {isLogged, requiresAuth, registerIfNeeded}
}

export default useAuthBeforeLead
